// Brand-page data sources card. Hidden unless VITE_DATA_SOURCES=true.
// Shows each source's path (direct or Supermetrics), last sync, and health.

import { useEffect, useState } from 'react';
import { Badge, Card, CardBody, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { dataSourcesEnabled, getDataSources, type DataSourceView } from '../../marketingPlans/dataSources';

export function DataSourcesCard({ brandId, provider }: { brandId: string; provider?: string }) {
  const enabled = dataSourcesEnabled();
  const [loading, setLoading] = useState(enabled);
  const [sources, setSources] = useState<DataSourceView[] | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    void getDataSources(brandId).then((result) => {
      if (cancelled) return;
      setSources(result.sources);
      setUnavailable(result.unavailableReason);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [enabled, brandId]);

  if (!enabled) {
    // Unfiltered card stays hidden when the flag is off. A provider
    // section still has to say the source is not available.
    if (!provider) return null;
    return (
      <Card variant="outline" data-testid="data-sources-card">
        <CardBody>
          <Text fontWeight="700" color="brand.ink" mb={3}>{provider}</Text>
          <Text fontSize="sm" color="brand.muted" data-testid="data-sources-unavailable">
            {provider} shows up when data connections are available.
          </Text>
        </CardBody>
      </Card>
    );
  }

  const visible = sources == null
    ? null
    : (provider
      ? sources.filter((source) => source.provider.toLowerCase() === provider.toLowerCase())
      : sources);

  return (
    <Card variant="outline" data-testid="data-sources-card">
      <CardBody>
        <Text fontWeight="700" color="brand.ink" mb={3}>{provider ?? 'Data sources'}</Text>
        {loading && (
          <HStack>
            <Spinner size="sm" />
            <Text fontSize="sm" color="brand.muted">Loading data sources…</Text>
          </HStack>
        )}
        {!loading && unavailable && (
          <Text fontSize="sm" color="brand.muted" data-testid="data-sources-unavailable">{unavailable}</Text>
        )}
        {!loading && !provider && visible && visible.length === 0 && (
          <Text fontSize="sm" color="brand.muted">No data sources recorded.</Text>
        )}
        {!loading && !!provider && !unavailable && visible && visible.length === 0 && (
          <Text fontSize="sm" color="brand.muted" data-testid="data-source-missing">Not connected</Text>
        )}
        {!loading && visible && visible.length > 0 && (
          <VStack align="stretch" spacing={2}>
            {visible.map((source, index) => (
              <HStack key={`${source.provider}-${index}`} justify="space-between" align="flex-start" spacing={3} data-testid="data-source-row">
                <VStack align="stretch" spacing={0} minW={0}>
                  <Text fontSize="sm" fontWeight="700">{source.provider}</Text>
                  <Text fontSize="xs" color="brand.muted">
                    {source.connected
                      ? (source.lastSync ? `Last sync ${source.lastSync}` : 'No sync yet')
                      : 'Not connected'}
                    {source.neededByPlan ? ' · Needed by the plan' : ''}
                  </Text>
                  {source.problem && <Text fontSize="xs" color="brand.muted">{source.problem}</Text>}
                </VStack>
                <HStack spacing={2} flexShrink={0}>
                  {source.connected && source.path ? <Badge variant="subtle">{source.path}</Badge> : null}
                  <Badge colorScheme={healthColor(source.health)} variant="subtle">{source.health}</Badge>
                </HStack>
              </HStack>
            ))}
          </VStack>
        )}
      </CardBody>
    </Card>
  );
}

function healthColor(health: string): string {
  if (health === 'Healthy' || health === 'Connected') return 'green';
  if (health === 'Warning' || health === 'Expiring') return 'yellow';
  if (health === 'Error') return 'red';
  return 'gray';
}
